import { useId, type KeyboardEvent } from 'react';
import { Modal } from 'react-daisyui';
import { Button } from '@leadsales/design-system/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import type { tTopUpOptionsFailureKind } from '../TopUpModal/TopUpModal.types';
import '../../Wallet.confirmDialog.styles.scss';

export type tTopUpOptionsErrorModalProps = {
  isOpen: boolean;
  kind: tTopUpOptionsFailureKind;
  onClose: () => void;
};

/**
 * Lo que ve el cliente cuando «Recargar» no puede cargar las opciones
 * (RET-2280).
 *
 * Antes no veía nada: el modal de recarga devolvía `null` si las opciones no
 * llegaban, y un 409 de wallet-service (cliente de Stripe ambiguo) dejaba el
 * botón sin respuesta. MexQ le dio clic nueve veces antes de escribir a soporte.
 *
 * Dos textos, porque las dos fallas piden cosas distintas:
 * - `terminal`: la wallet no puede resolver a quién cobrarle. Reintentar no lo
 *   arregla, así que el texto manda a soporte y no invita a volver a pulsar.
 * - `transient`: la red o un servicio no respondió. Volver a abrir el modal en
 *   unos minutos sí puede funcionar.
 *
 * Copia la forma de `AutoTopUpOffModal` (no hay un componente compartido de
 * diálogo del saldo). Como aquí no hay nada que confirmar, tiene una sola
 * salida, y la caja lleva la semántica de `alertdialog` que `Modal.Legacy` no
 * pone por sí solo.
 */
export default function TopUpOptionsErrorModal(
  props: tTopUpOptionsErrorModalProps,
) {
  const { isOpen, kind, onClose } = props;
  const { t } = useTranslation('wallet');
  const titleId = useId();
  const bodyId = useId();

  if (!isOpen) return null;

  // `Modal.Legacy` no escucha Escape. El foco vive en «Entendido», dentro de la
  // caja, así que la tecla llega aquí por burbujeo.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') onClose();
  };

  return (
    <Modal.Legacy
      open={isOpen}
      onClickBackdrop={onClose}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      onKeyDown={handleKeyDown}
      className="WalletConfirmDialog max-w-[27.5rem] rounded-2xl p-6"
    >
      <div
        className="WalletConfirmDialog__Body"
        data-testid="wallet-top-up-options-error-modal"
      >
        <span
          className="WalletConfirmDialog__Icon WalletConfirmDialog__Icon--warning"
          aria-hidden="true"
        >
          <ExclamationTriangleIcon />
        </span>

        <h3 id={titleId} className="WalletConfirmDialog__Title">
          {t('topUp.optionsError.title')}
        </h3>

        <p
          id={bodyId}
          className="WalletConfirmDialog__Text"
          data-testid="wallet-top-up-options-error-body"
        >
          {t(`topUp.optionsError.${kind}`)}
        </p>

        <div className="WalletConfirmDialog__Actions">
          <Button
            color="primary"
            size="md"
            onClick={onClose}
            autoFocus
            data-testid="wallet-top-up-options-error-close"
          >
            {t('topUp.optionsError.close')}
          </Button>
        </div>
      </div>
    </Modal.Legacy>
  );
}
